import Boom from '@hapi/boom';
import { Boom as InternalBoom } from '@meshwatch/types';

import { FormErrorKeys, FormErrorsBoom } from '../validation/types';

export function validationBoom<T>(errors?: T): InternalBoom<T> {
  return newBoom<T>(400, 'Something went wrong while validating your payload', errors);
}

export function boomify<T>(
  error: Error,
  options: { statusCode?: number; message?: string; override?: boolean } = {}
): InternalBoom<T> {
  let statusCode = options.statusCode;
  if (error instanceof ServiceException) {
    statusCode = error.httpStatusCode;
  }

  const boom = Boom.boomify(error, {
    ...options,
    statusCode,
  });

  const payload = {
    ...boom.output.payload,
    message: error.message || boom.output.payload.message,
  };

  return fromBoomPayload(payload);
}

export function isBoom<T, K = undefined | FormErrorKeys<T>>(
  body: T | FormErrorsBoom<K>
): body is FormErrorsBoom<K> {
  const formErrorsBoom = body as FormErrorsBoom<K>;
  return formErrorsBoom.error !== undefined;
}

function newBoom<T>(statusCode: number, message?: string, errors?: T): InternalBoom<T> {
  const boom = new Boom(message, {
    statusCode,
  });
  return fromBoomPayload<T>(boom.output.payload, errors);
}

function fromBoomPayload<T>(boomPayload: Boom.Payload, errors?: T): InternalBoom<T> {
  return errors
    ? {
        ...boomPayload,
        errors,
      }
    : boomPayload;
}

abstract class ServiceException extends Error {
  public httpStatusCode: number;

  public constructor(message: string, httpStatusCode: number, name: string) {
    super(message);
    this.name = name;
    this.httpStatusCode = httpStatusCode;
    this.constructor = ServiceException;
    Error.captureStackTrace(this, ServiceException);
    Object.setPrototypeOf(this, ServiceException.prototype);
  }
}

export class DatabaseException extends ServiceException {
  public constructor(message: string) {
    super(message, 500, 'DatabaseException');
  }
}

export class NotFoundException extends ServiceException {
  public constructor(message: string) {
    super(message, 404, 'NotFoundException');
  }
}

export class ForbiddenException extends ServiceException {
  public constructor(message: string) {
    super(message, 403, 'ForbiddenException');
  }
}

export class BadRequestException extends ServiceException {
  public constructor(message: string) {
    super(message, 400, 'BadRequestException');
  }
}

export class UnreachableCaseError extends ServiceException {
  public constructor(val: never) {
    super(`Unreachable case: ${val}`, 500, 'UnreachableCaseError');
  }
}
